/**
 * 登录页面：电商批量生图平台专业登录界面
 * 支持密码登录和验证码登录两种模式，带有精美动画效果
 */

import type { ReactElement } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Phone,
  Zap,
  Shield,
  BarChart3,
  Image,
  CheckCircle2,
  UserPlus,
  Sparkles,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores'
import { generateCaptcha } from '@/services/authService'
import type { CaptchaResponse } from '@/types/auth'

/**
 * 登录模式类型
 */
type LoginMode = 'password' | 'sms'

/**
 * 验证码倒计时秒数
 */
const SMS_COUNTDOWN_SECONDS = 60 as const

/**
 * 密码最小长度
 */
const MIN_PASSWORD_LENGTH = 6 as const

/**
 * 手机号长度
 */
const PHONE_NUMBER_LENGTH = 11 as const

/**
 * 验证码长度
 */
const SMS_CODE_LENGTH = 6 as const

/**
 * 图形验证码长度
 */
const CAPTCHA_CODE_LENGTH = 4 as const

/**
 * 新用户注册赠送算力
 */
const SIGNUP_BONUS_CREDITS = 100 as const

/**
 * 校验中国大陆手机号格式（简单校验）
 * @param phone 手机号
 * @returns 是否合法
 */
function isValidCnPhone(phone: string): boolean {
  return /^1\d{10}$/.test(phone)
}

/**
 * 校验验证码格式（6 位数字）
 * @param code 验证码
 * @returns 是否合法
 */
function isValidSmsCode(code: string): boolean {
  return new RegExp(`^\\d{${String(SMS_CODE_LENGTH)}}$`).test(code)
}

/**
 * 校验密码格式（至少 6 位）
 * @param password 密码
 * @returns 是否合法
 */
function isValidPassword(password: string): boolean {
  return password.length >= MIN_PASSWORD_LENGTH
}

/**
 * 校验图形验证码格式（4 位字母或数字）
 * @param captcha 图形验证码
 * @returns 是否合法
 */
function isValidCaptchaCode(captcha: string): boolean {
  return new RegExp(`^[a-zA-Z0-9]{${String(CAPTCHA_CODE_LENGTH)}}$`).test(captcha)
}

/**
 * 仅保留数字字符，并限制最大长度
 * @param value 输入值
 * @param maxLength 最大长度
 * @returns 处理后的数字字符串
 */
function keepDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength)
}

/**
 * 仅保留字母和数字字符，并限制最大长度
 * @param value 输入值
 * @param maxLength 最大长度
 * @returns 处理后的字符串
 */
function keepAlphanumeric(value: string, maxLength: number): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').slice(0, maxLength)
}

/**
 * 特性卡片数据
 */
const FEATURES = [
  {
    icon: Zap,
    title: '极速处理',
    desc: '批量任务秒级响应',
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  {
    icon: Shield,
    title: '稳定可靠',
    desc: '99.9% 服务可用性',
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
  },
  {
    icon: BarChart3,
    title: '效率提升',
    desc: '节省 80% 操作时间',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  {
    icon: Image,
    title: '批量生图',
    desc: '一键处理千张图片',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
  },
] as const

/**
 * 动画浮动圆形背景组件
 * @returns 装饰性动画背景
 */
function FloatingOrbs(): ReactElement {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* 主色调大圆 */}
      <motion.div
        className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl"
        animate={{
          x: [0, 30, 0],
          y: [0, -20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      {/* 紫色中圆 */}
      <motion.div
        className="absolute right-0 top-1/4 h-72 w-72 rounded-full bg-purple-500/15 blur-3xl"
        animate={{
          x: [0, -40, 0],
          y: [0, 30, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />
      {/* 琥珀色小圆 */}
      <motion.div
        className="absolute bottom-20 left-1/4 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl"
        animate={{
          x: [0, 50, 0],
          y: [0, -30, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />
      {/* 底部装饰大圆 */}
      <motion.div
        className="absolute -bottom-48 -right-32 h-[500px] w-[500px] rounded-full bg-primary/10 blur-3xl"
        animate={{
          x: [0, -30, 0],
          y: [0, 20, 0],
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 9,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5,
        }}
      />
    </div>
  )
}

/**
 * 网格背景装饰
 * @returns 网格背景元素
 */
function GridPattern(): ReactElement {
  return (
    <div className="absolute inset-0 overflow-hidden opacity-[0.03]">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(to right, currentColor 1px, transparent 1px),
            linear-gradient(to bottom, currentColor 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
    </div>
  )
}

/**
 * 统计数字组件
 * @param props 组件属性
 * @returns 统计展示元素
 */
function StatNumber({
  value,
  label,
  delay = 0,
}: {
  value: string
  label: string
  delay?: number
}): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="text-center"
    >
      <div className="text-3xl font-bold text-foreground">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </motion.div>
  )
}

/**
 * 登录页面组件
 * @returns 页面 ReactElement
 */
export function LoginPage(): ReactElement {
  const navigate = useNavigate()

  // Store actions
  const loginByPassword = useAuthStore((state) => state.loginByPassword)
  const loginBySms = useAuthStore((state) => state.loginBySms)
  const sendSmsCode = useAuthStore((state) => state.sendSmsCode)
  const isLoading = useAuthStore((state) => state.isLoading)
  const storeError = useAuthStore((state) => state.error)
  const clearError = useAuthStore((state) => state.clearError)

  // 表单状态
  const [mode, setMode] = useState<LoginMode>('password')
  const [phone, setPhone] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [touched, setTouched] = useState<boolean>(false)
  const [localError, setLocalError] = useState<string | null>(null)

  // 验证码倒计时
  const [countdown, setCountdown] = useState<number>(0)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 图形验证码状态
  const [captchaData, setCaptchaData] = useState<CaptchaResponse | null>(null)
  const [captchaCode, setCaptchaCode] = useState<string>('')
  const [captchaLoading, setCaptchaLoading] = useState<boolean>(false)

  // 记住我状态
  const [rememberMe, setRememberMe] = useState<boolean>(true)

  // 综合错误信息
  const error: string | null = localError ?? storeError

  /**
   * 清理倒计时定时器
   */
  const clearCountdown = useCallback((): void => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [])

  /**
   * 开始验证码倒计时
   */
  const startCountdown = useCallback((): void => {
    clearCountdown()
    setCountdown(SMS_COUNTDOWN_SECONDS)

    countdownRef.current = setInterval((): void => {
      setCountdown((prev: number): number => {
        if (prev <= 1) {
          clearCountdown()
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [clearCountdown])

  // 组件卸载时清理定时器
  useEffect((): (() => void) => {
    return (): void => {
      clearCountdown()
    }
  }, [clearCountdown])

  // 切换模式时清空错误
  useEffect((): void => {
     
    setLocalError(null)
    clearError()
    setTouched(false)
  }, [mode, clearError])

  /**
   * 获取图形验证码
   */
  const fetchCaptcha = useCallback(async (): Promise<void> => {
    setCaptchaLoading(true)
    setCaptchaCode('')
    try {
      const data = await generateCaptcha()
      setCaptchaData(data)
    } catch {
      setLocalError('获取验证码失败，请点击刷新')
    } finally {
      setCaptchaLoading(false)
    }
  }, [])

  // 页面加载时获取验证码
  useEffect((): void => {
    void fetchCaptcha()
  }, [fetchCaptcha])

  /**
   * 手机号校验错误
   */
  const phoneError: string | null = useMemo((): string | null => {
    if (!touched) {
      return null
    }
    if (phone.length === 0) {
      return '请输入手机号'
    }
    if (!isValidCnPhone(phone)) {
      return '手机号格式不正确'
    }
    return null
  }, [phone, touched])

  /**
   * 密码校验错误
   */
  const passwordError: string | null = useMemo((): string | null => {
    if (mode !== 'password') {
      return null
    }
    if (!touched) {
      return null
    }
    if (password.length === 0) {
      return '请输入密码'
    }
    if (!isValidPassword(password)) {
      return `密码至少 ${String(MIN_PASSWORD_LENGTH)} 位`
    }
    return null
  }, [password, touched, mode])

  /**
   * 验证码校验错误
   */
  const codeError: string | null = useMemo((): string | null => {
    if (mode !== 'sms') {
      return null
    }
    if (!touched) {
      return null
    }
    if (code.length === 0) {
      return '请输入验证码'
    }
    if (!isValidSmsCode(code)) {
      return `验证码为 ${String(SMS_CODE_LENGTH)} 位数字`
    }
    return null
  }, [code, touched, mode])

  /**
   * 图形验证码校验错误
   */
  const captchaError: string | null = useMemo((): string | null => {
    if (!touched) {
      return null
    }
    if (captchaCode.length === 0) {
      return '请输入图形验证码'
    }
    if (!isValidCaptchaCode(captchaCode)) {
      return `图形验证码为 ${String(CAPTCHA_CODE_LENGTH)} 位字母或数字`
    }
    return null
  }, [captchaCode, touched])

  /**
   * 表单是否可提交
   */
  const canSubmit: boolean = useMemo((): boolean => {
    if (!isValidCnPhone(phone)) {
      return false
    }
    if (!isValidCaptchaCode(captchaCode)) {
      return false
    }
    if (captchaData === null) {
      return false
    }
    if (mode === 'password') {
      return isValidPassword(password)
    }
    return isValidSmsCode(code)
  }, [phone, password, code, captchaCode, captchaData, mode])

  /**
   * 发送验证码
   */
  const handleSendCode = useCallback(async (): Promise<void> => {
    if (!isValidCnPhone(phone)) {
      setLocalError('请输入正确的手机号')
      return
    }

    if (countdown > 0) {
      return
    }

    setLocalError(null)

    try {
      await sendSmsCode(phone)
      startCountdown()
    } catch {
      // 错误已在 store 中处理
    }
  }, [phone, countdown, sendSmsCode, startCountdown])

  /**
   * 处理登录提交
   */
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      setTouched(true)
      setLocalError(null)

      if (!canSubmit) {
        return
      }

      if (captchaData === null) {
        setLocalError('请先获取图形验证码')
        return
      }

      try {
        if (mode === 'password') {
          await loginByPassword({
            phone,
            password,
            captchaId: captchaData.captchaId,
            captchaCode,
            rememberMe,
          })
        } else {
          await loginBySms({
            phone,
            code,
            captchaId: captchaData.captchaId,
            captchaCode,
            rememberMe,
          })
        }
        void navigate('/')
      } catch {
        // 错误已在 store 中处理，登录失败时刷新验证码
        void fetchCaptcha()
      }
    },
    [canSubmit, mode, phone, password, code, captchaData, captchaCode, rememberMe, loginByPassword, loginBySms, navigate, fetchCaptcha]
  )

  /**
   * 切换密码可见性
   */
  const togglePasswordVisibility = useCallback((): void => {
    setShowPassword((prev: boolean): boolean => !prev)
  }, [])

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* 动画背景 */}
      <FloatingOrbs />
      <GridPattern />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl">
        {/* 左侧品牌展示区 */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="relative hidden flex-1 flex-col justify-between p-12 lg:flex"
        >
          {/* Logo 区域 */}
          <div>
            <Link to="/" className="inline-flex items-center gap-3">
              <motion.div
                className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary shadow-lg shadow-primary/25"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <Image className="h-6 w-6 text-primary-foreground" />
              </motion.div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">月汐AI</h1>
                <p className="text-sm text-muted-foreground">电商图片智能处理平台</p>
              </div>
            </Link>
          </div>

          {/* 核心价值主张 */}
          <div className="max-w-lg space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-4xl font-bold leading-tight tracking-tight">
                让电商图片处理
                <br />
                <span className="bg-gradient-to-r from-primary via-purple-500 to-primary bg-clip-text text-transparent">
                  更高效、更稳定
                </span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                专为电商卖家打造的批量生图平台，一站式解决去背景、放大、
                去水印、修复增强等图片处理需求。
              </p>
            </motion.div>

            {/* 特性网格 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-2 gap-4"
            >
              {FEATURES.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className="group rounded-2xl border bg-card/50 p-5 backdrop-blur-sm transition-shadow hover:shadow-lg"
                >
                  <div
                    className={cn(
                      'inline-flex h-10 w-10 items-center justify-center rounded-xl transition-transform group-hover:scale-110',
                      feature.bg
                    )}
                  >
                    <feature.icon className={cn('h-5 w-5', feature.color)} />
                  </div>
                  <h3 className="mt-3 font-semibold">{feature.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{feature.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* 统计数据 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex items-center justify-between rounded-2xl border bg-card/50 p-6 backdrop-blur-sm"
            >
              <StatNumber value="100万+" label="已处理图片" delay={0.9} />
              <div className="h-12 w-px bg-border" />
              <StatNumber value="5000+" label="活跃用户" delay={1.0} />
              <div className="h-12 w-px bg-border" />
              <StatNumber value="99.9%" label="服务稳定性" delay={1.1} />
            </motion.div>
          </div>

          {/* 底部信息 */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="text-sm text-muted-foreground"
          >
            已有数千家电商卖家的信赖之选
          </motion.p>
        </motion.div>

        {/* 右侧登录表单 */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative flex w-full items-center justify-center p-6 lg:w-[520px] lg:p-12"
        >
          <div className="w-full max-w-md">
            {/* 移动端 Logo */}
            <div className="mb-8 flex items-center justify-center lg:hidden">
              <Link to="/" className="inline-flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/25">
                  <Image className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="text-lg font-bold">月汐AI</span>
              </Link>
            </div>

            {/* 欢迎信息 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-8 text-center lg:text-left"
            >
              <h2 className="text-2xl font-bold tracking-tight">登录 / 注册</h2>
              <p className="mt-2 text-muted-foreground">
                输入手机号即可登录，新用户自动注册并赠送 {SIGNUP_BONUS_CREDITS} 算力
              </p>
            </motion.div>

            {/* 登录卡片 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="rounded-3xl border bg-card/80 p-8 shadow-xl shadow-black/5 backdrop-blur-sm"
            >
              {/* 自动注册提示横幅 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.35 }}
                className="mb-6 flex items-center gap-3 rounded-2xl border border-primary/25 bg-gradient-to-r from-primary/10 via-primary/5 to-amber-500/10 px-4 py-4 shadow-sm"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    新用户注册即送 {SIGNUP_BONUS_CREDITS} 算力
                  </p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    无需单独注册，首次登录自动创建账户并发放新手算力，登录后即可直接开始使用
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1 rounded-full bg-background/80 px-2.5 py-1 text-xs font-semibold text-primary shadow-sm">
                  <Sparkles className="h-3.5 w-3.5" />
                  限时福利
                </div>
              </motion.div>

              {/* 模式切换 */}
              <div className="mb-6 flex rounded-xl bg-muted/50 p-1">
                {(['password', 'sms'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={(): void => { setMode(m); }}
                    className={cn(
                      'relative flex-1 rounded-lg py-2.5 text-sm font-medium transition-colors',
                      mode === m ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {mode === m && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 rounded-lg bg-background shadow-sm"
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">
                      {m === 'password' ? '密码登录' : '验证码登录'}
                    </span>
                  </button>
                ))}
              </div>

              {/* 错误提示 */}
              <AnimatePresence mode="wait">
                {error !== null && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 overflow-hidden"
                  >
                    <div className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                      {error}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 登录表单 */}
              <form
                className="space-y-5"
                onSubmit={(event): void => {
                  void handleSubmit(event)
                }}
              >
                {/* 手机号输入 */}
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="phone">
                    手机号
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={PHONE_NUMBER_LENGTH}
                      placeholder={`请输入 ${String(PHONE_NUMBER_LENGTH)} 位手机号`}
                      className={cn(
                        'h-12 w-full rounded-xl border bg-background pl-11 pr-4 text-sm outline-none transition-all',
                        'placeholder:text-muted-foreground/60',
                        'focus:border-primary focus:ring-2 focus:ring-primary/20',
                        phoneError !== null && 'border-destructive focus:border-destructive focus:ring-destructive/20'
                      )}
                      onChange={(event): void => {
                        setPhone(keepDigits(event.target.value, PHONE_NUMBER_LENGTH))
                      }}
                    />
                  </div>
                  <AnimatePresence>
                    {phoneError !== null && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-xs text-destructive"
                      >
                        {phoneError}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {/* 密码/验证码输入 */}
                <AnimatePresence mode="wait">
                  {mode === 'password' ? (
                    <motion.div
                      key="password"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-2"
                    >
                      <label className="text-sm font-medium" htmlFor="password">
                        密码
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                          <Lock className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          autoComplete="current-password"
                          placeholder="请输入密码"
                          className={cn(
                            'h-12 w-full rounded-xl border bg-background pl-11 pr-12 text-sm outline-none transition-all',
                            'placeholder:text-muted-foreground/60',
                            'focus:border-primary focus:ring-2 focus:ring-primary/20',
                            passwordError !== null &&
                              'border-destructive focus:border-destructive focus:ring-destructive/20'
                          )}
                          onChange={(event): void => {
                            setPassword(event.target.value)
                          }}
                        />
                        <button
                          type="button"
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                          onClick={togglePasswordVisibility}
                          aria-label={showPassword ? '隐藏密码' : '显示密码'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <AnimatePresence>
                        {passwordError !== null && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-xs text-destructive"
                          >
                            {passwordError}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="sms"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-2"
                    >
                      <label className="text-sm font-medium" htmlFor="code">
                        验证码
                      </label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                            <Lock className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <input
                            id="code"
                            value={code}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={SMS_CODE_LENGTH}
                            placeholder={`${String(SMS_CODE_LENGTH)} 位验证码`}
                            className={cn(
                              'h-12 w-full rounded-xl border bg-background pl-11 pr-4 text-sm outline-none transition-all',
                              'placeholder:text-muted-foreground/60',
                              'focus:border-primary focus:ring-2 focus:ring-primary/20',
                              codeError !== null &&
                                'border-destructive focus:border-destructive focus:ring-destructive/20'
                            )}
                            onChange={(event): void => {
                              setCode(keepDigits(event.target.value, SMS_CODE_LENGTH))
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          className="h-12 shrink-0 px-5"
                          disabled={!isValidCnPhone(phone) || countdown > 0}
                          onClick={(): void => {
                            void handleSendCode()
                          }}
                        >
                          {countdown > 0 ? `${String(countdown)}s` : '获取验证码'}
                        </Button>
                      </div>
                      <AnimatePresence>
                        {codeError !== null && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="text-xs text-destructive"
                          >
                            {codeError}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* 图形验证码输入 */}
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="captcha">
                    图形验证码
                  </label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <input
                        id="captcha"
                        value={captchaCode}
                        autoComplete="off"
                        maxLength={CAPTCHA_CODE_LENGTH}
                        placeholder={`${String(CAPTCHA_CODE_LENGTH)} 位验证码`}
                        className={cn(
                          'h-12 w-full rounded-xl border bg-background pl-11 pr-4 text-sm outline-none transition-all',
                          'placeholder:text-muted-foreground/60',
                          'focus:border-primary focus:ring-2 focus:ring-primary/20',
                          captchaError !== null &&
                            'border-destructive focus:border-destructive focus:ring-destructive/20'
                        )}
                        onChange={(event): void => {
                          setCaptchaCode(keepAlphanumeric(event.target.value, CAPTCHA_CODE_LENGTH))
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className={cn(
                        'relative h-12 w-28 shrink-0 overflow-hidden rounded-xl border bg-muted/30 transition-all',
                        'hover:border-primary/50 hover:shadow-sm',
                        captchaLoading && 'cursor-wait opacity-70'
                      )}
                      onClick={(): void => {
                        void fetchCaptcha()
                      }}
                      disabled={captchaLoading}
                      title="点击刷新验证码"
                      aria-label="刷新验证码"
                    >
                      {captchaData !== null ? (
                        <img
                          src={captchaData.imageBase64}
                          alt="图形验证码"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                          {captchaLoading ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </motion.div>
                          ) : (
                            <span className="flex items-center gap-1">
                              <RefreshCw className="h-3 w-3" />
                              点击获取
                            </span>
                          )}
                        </div>
                      )}
                      {captchaData !== null && !captchaLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all hover:bg-black/20 hover:opacity-100">
                          <RefreshCw className="h-4 w-4 text-white drop-shadow" />
                        </div>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <AnimatePresence>
                      {captchaError !== null && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="text-xs text-destructive"
                        >
                          {captchaError}
                        </motion.p>
                      )}
                    </AnimatePresence>
                    <button
                      type="button"
                      className="ml-auto text-xs text-muted-foreground transition-colors hover:text-primary"
                      onClick={(): void => {
                        void fetchCaptcha()
                      }}
                      disabled={captchaLoading}
                    >
                      看不清？换一张
                    </button>
                  </div>
                </div>

                {/* 记住我选项 */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={rememberMe}
                    onClick={(): void => { setRememberMe((prev: boolean): boolean => !prev); }}
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                      rememberMe
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground/30 bg-background hover:border-primary/50'
                    )}
                  >
                    {rememberMe && (
                      <CheckCircle2 className="h-3 w-3" />
                    )}
                  </button>
                  <label
                    className="cursor-pointer text-sm text-muted-foreground"
                    onClick={(): void => { setRememberMe((prev: boolean): boolean => !prev); }}
                  >
                    记住登录
                  </label>
                </div>

                {/* 提交按钮 */}
                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <Button
                    type="submit"
                    size="lg"
                    className="h-12 w-full gap-2 rounded-xl text-base font-medium shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <motion.div
                        className="h-5 w-5 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                    ) : (
                      <>
                        登录 / 注册
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </motion.div>
              </form>

              {/* 特性提示 */}
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    安全加密
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    注册送 {SIGNUP_BONUS_CREDITS} 算力
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    即时开通
                  </span>
                </div>
                <p className="text-center text-xs text-muted-foreground/80">
                  新用户首次登录自动注册，无需额外步骤，注册成功立即到账 {SIGNUP_BONUS_CREDITS} 算力
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
